<script lang="ts">
import { defineComponent } from "vue";
import { mapGetters } from "vuex";

import { Statsig } from "@/env/statsig/client";
import {
  getEmailCommunicationOptIn,
  setEmailCommunicationOptIn,
} from "@/services/profileService/mailingList";
import { sendButtonCtaTelemetry } from "@/utils/ctaTelemetry";
import isUnauth from "@/utils/isUnauth";
import leftShapeBadges from "@/views/ForYou/assets/leftShapeBadges.svg";
import rightShapeBadges from "@/views/ForYou/assets/rightShapeBadges.svg";

export default defineComponent({
  name: "NewsletterBanner",

  data() {
    return {
      consentChecked: false,
      gateEnabledEmailCommunication: false,
      isSubscribed: false,
      isSubmitting: false,
      submitSuccess: false,
      submitError: false,
      leftShapeBadges,
      rightShapeBadges,
    };
  },

  computed: {
    isUnauth,
    ...mapGetters({
      userStatus: "user/status",
    }),
    userEmail(): string {
      return this.userStatus?.email || "";
    },
    showBanner(): boolean {
      return (
        this.gateEnabledEmailCommunication &&
        !this.isUnauth &&
        (!this.isSubscribed || this.submitSuccess)
      );
    },
  },

  async mounted() {
    if (this.isUnauth) return;

    this.gateEnabledEmailCommunication = await Statsig.featureGate(
      "EMAIL_NOTIFICATION_OPT_IN"
    );
    if (!this.gateEnabledEmailCommunication) return;

    if (this.userEmail) {
      try {
        this.isSubscribed = await getEmailCommunicationOptIn(this.userEmail);
      } catch {
        this.isSubscribed = false;
      }
    }
  },

  methods: {
    async handleSubmit() {
      if (!this.consentChecked || this.isSubmitting || !this.userEmail) {
        return;
      }

      this.isSubmitting = true;
      this.submitError = false;

      try {
        await setEmailCommunicationOptIn(this.userEmail);
        this.submitSuccess = true;
        this.isSubscribed = true;
        await sendButtonCtaTelemetry(
          this.$route.fullPath,
          "Newsletter Subscribe",
          false
        );
      } catch {
        this.submitError = true;
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
</script>

<template>
  <div
    v-if="showBanner"
    data-cy="newsletter-banner"
    class="relative my-16 py-10"
  >
    <!-- Left decorative shapes — pinned to left edge of viewport -->
    <img
      :src="leftShapeBadges"
      alt=""
      aria-hidden="true"
      class="pointer-events-none absolute left-10 hidden -translate-y-1/2 md:block"
      style="width: 136px; height: 156px; top: 50%"
    />

    <!-- Right decorative shapes — pinned to right edge of viewport -->
    <img
      :src="rightShapeBadges"
      alt=""
      aria-hidden="true"
      class="pointer-events-none absolute right-32 hidden -translate-y-1/2 md:block"
      style="width: 136px; height: 156px; top: 50%"
    />

    <!-- Content -->
    <div
      class="relative z-10 mx-4 flex flex-col items-start justify-between gap-8 md:mx-6 md:flex-row md:items-center xxl:mx-20 xlg:mx-auto xlg:max-w-[100rem]"
    >
      <!-- Left content: SVG spacer + text -->
      <div class="flex shrink-0 items-center gap-4 md:gap-10 md:w-[38%]">
        <!-- Spacer to clear left SVG -->
        <div class="hidden shrink-0 md:block" style="width: 100px"></div>
        <div class="pr-10">
          <h2
            class="text-2xl font-bold leading-tight text-black-dark md:text-[28px]"
          >
            Be the first to know about Cisco learning
          </h2>
          <p class="mt-3 text-base leading-relaxed text-black-light">
            Stay up to date with our latest news, exclusive offers, and
            more–straight to your inbox.
          </p>
        </div>
      </div>

      <!-- Right content: checkbox + button + SVG spacer -->
      <div class="flex items-start gap-4 md:gap-6 md:w-[55%]">
        <div class="flex grow flex-col items-start gap-5 pr-[140px]">
          <div v-if="submitSuccess" class="font-medium text-green-700">
            Thank you for subscribing!
          </div>
          <template v-else>
            <label
              for="newsletter-consent"
              class="flex cursor-pointer items-start gap-3"
            >
              <input
                id="newsletter-consent"
                v-model="consentChecked"
                type="checkbox"
                data-cy="newsletter-consent-checkbox"
                class="mt-0.5 h-5 w-5 shrink-0 cursor-pointer appearance-none rounded-sm border-2 border-black-light checked:border-blue-light checked:bg-blue-light"
              />
              <span class="text-sm leading-snug text-black-dark">
                I would like to receive emails about Learn with Cisco programs.
                I understand I can unsubscribe at any time.
              </span>
            </label>

            <button
              data-cy="newsletter-submit-btn"
              :disabled="!consentChecked || isSubmitting"
              class="rounded-full bg-blue-light px-8 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
              @click="handleSubmit"
              @keyup.enter="handleSubmit"
            >
              {{ isSubmitting ? "Submitting..." : "Submit" }}
            </button>

            <p v-if="submitError" class="text-sm text-red-600" role="alert">
              Something went wrong. Please try again.
            </p>
          </template>
        </div>
        <!-- Spacer to clear right SVG -->
        <div class="hidden shrink-0 md:block" style="width: 100px"></div>
      </div>
    </div>
  </div>
</template>
